import { create } from 'zustand';
import { useAppStore } from './useAppStore';
import { fmtNow } from '../data/format';
import { seatedCount, billSubOf, orderStatusFromDoc } from '../data/derive';
import type {
  FloorTableState, WaitlistEntry, Reservation, Draft, Order, Ticket,
  SectionCfg, LocationCfg, SettingsCfg, FeedEvent, CombinePolicy,
} from '../data/seed';
import type { ToastTone } from '../types';

/**
 * Central hospitality state — a faithful port of the prototype's `class Component`
 * (SK Hospitality.dc.html lines 1023-1717). The config slices (location/sections/
 * tables/settings) persist in the adapter; the transactional slices are seeded from
 * the mockup and stream from the SK backbone in production. Toasts are delegated to
 * useAppStore (already wired to <ToastHost/>).
 */

// v4: seat-based sizing + padded-footprint shelf-packing (zones never overlap).
// Bumping the key retires positions saved by an older layout so it applies clean.
const FLOOR_POS_KEY = 'skFloorPositions.v4';

/**
 * A tile's on-canvas size, derived from its seat count + shape (the prototype
 * gets its varied look from per-table pixel sizes; real tables default to a flat
 * 74×74, so we scale here instead). Bigger party → bigger tile; communal/
 * rectangle tables are wide-and-short (e.g. an 8-seat bench). Kept in one place
 * so the drag canvas, the shelf-packer and the tiles all agree on dimensions.
 */
const clampN = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
export function tileDims(tb: { seats?: number; shape?: string; communal?: boolean }): { w: number; h: number } {
  const seats = tb.seats || 2;
  const wide = tb.shape === 'communal' || tb.shape === 'rectangle' || tb.communal === true;
  if (wide) return { w: clampN(Math.round(96 + seats * 8), 128, 220), h: 76 };
  const side = clampN(Math.round(40 + seats * 8), 52, 116); // 2→56, 4→72, 6→88, 8→104
  return { w: side, h: side };
}

// Zone bounding-box padding — must match the dashed zone drawn in Floor.tsx
// (tile bbox + side/bottom padding + a top label band) so collision detection
// operates on exactly the rectangle the operator sees.
const ZONE_PAD = 12, ZONE_HEAD = 18;
// Minimum gap enforced between two zone bounding boxes once a collision is
// resolved (spec: "configurable padding, for example 24px").
const SECTION_GAP = 24;

/** A section's dashed-zone bounding box — the same formula Floor.tsx draws. */
export function sectionBBox(tiles: FloorTableState[]): { x: number; y: number; w: number; h: number } | null {
  if (!tiles.length) return null;
  const x = Math.max(0, Math.min(...tiles.map((tb) => tb.x)) - ZONE_PAD);
  const y = Math.max(0, Math.min(...tiles.map((tb) => tb.y)) - ZONE_PAD - ZONE_HEAD);
  const x2 = Math.max(...tiles.map((tb) => tb.x + tileDims(tb).w)) + ZONE_PAD;
  const y2 = Math.max(...tiles.map((tb) => tb.y + tileDims(tb).h)) + ZONE_PAD;
  return { x, y, w: x2 - x, h: y2 - y };
}

function loadPositions(): Record<string, { x: number; y: number }> {
  if (typeof localStorage === 'undefined') return {};
  try { return JSON.parse(localStorage.getItem(FLOOR_POS_KEY) || '{}'); } catch { return {}; }
}

// Dashboard KPIs + the SUKI feed are live, backend-sourced data — but a page
// reload nukes the in-memory store, so they'd flash back to zero/empty for
// the second or so until the SSE stream reconnects and replays. Cache the
// last-known values as a display seed only: the SSE handler (hospitalitySync)
// overwrites them the moment a real frame arrives, same "cache seeds the UI,
// live data is the actual source of truth" pattern used for auth elsewhere.
// Keyed by orgId — if a different org logs into hospitality on this same
// device, they must never see the previous org's cached revenue/calls/feed
// even for a flash, so each org gets its own slot instead of one shared key.
const DASH_CACHE_PREFIX = 'sk_hospitality.dashboardCache';
function dashCacheKey(): string {
  const orgId = useAppStore.getState().orgId;
  return orgId ? `${DASH_CACHE_PREFIX}.${orgId}` : DASH_CACHE_PREFIX;
}
interface DashCache {
  callsLive: number; ordersToday: number; ordersOnWay: number; upsellPct: number;
  revenueToday: number; revenueLive: boolean; tablesOccupied: number | null; events: FeedEvent[];
}
function loadDashCache(): Partial<DashCache> {
  if (typeof localStorage === 'undefined') return {};
  try { return JSON.parse(localStorage.getItem(dashCacheKey()) || '{}'); } catch { return {}; }
}
function saveDashCache(c: DashCache): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(dashCacheKey(), JSON.stringify(c)); } catch { /* noop */ }
}

/** True once any floor layout has been saved (auto-tidy or a manual drag). Used to
 *  decide whether to auto-arrange a fresh floor vs. respect the operator's layout. */
export function hasSavedFloorPositions(): boolean {
  return Object.keys(loadPositions()).length > 0;
}

// Saved drag positions (localStorage) are applied over backend-loaded tables in
// hydrateConfig; `defaultPos` records each table's loaded position so "Reset
// layout" can restore it. Both start empty — real tables arrive from the adapter.
const savedPos = loadPositions();
let defaultPos: Record<string, { x: number; y: number }> = {};
const savedDash = loadDashCache();

const toast = (message: string, tone: ToastTone = 'success') => useAppStore.getState().toast(message, tone);

export interface DispCounters { calls: number; orders: number; onway: number; occ: number; upsell: number; revenue: number; beat: number; bill: number; }
export type GuestView = 'join' | 'whatsapp';
export type GuestStep = 'qr' | 'phone' | 'otp' | 'queue';
export type QueueState = 'waiting' | 'ready';
export interface ResForm { size: number; section: string; name: string; phone: string; contactId?: string; }
export type SettingsTab = 'venue' | 'sections' | 'tables' | 'timing' | 'policies';

interface HospState {
  // live KPIs + eased display mirror
  callsLive: number; ordersToday: number; ordersOnWay: number; upsellPct: number; revenueToday: number;
  /** Live occupied-table count from the dashboard SSE stream (SK Calendar/Queue via the
   *  adapter) — null until the first frame arrives, so other terminals' seat/free actions
   *  show up here without a reload. Falls back to this session's own `tables` while null. */
  tablesOccupied: number | null;
  disp: DispCounters;
  events: FeedEvent[];

  // floor
  tables: FloorTableState[];
  selectedTableId: string | null;
  combineFrom: string | null;
  editLayout: boolean;
  draggingId: string | null;
  floorFilter: string; // 'all' | section name
  waitlist: WaitlistEntry[];

  // reservations
  reservations: Reservation[];
  draft: Draft | null;
  resForm: ResForm;
  resSlot: string | null;

  // orders
  activeOrderId: string;
  menuQuery: string;
  menuCat: string;
  orders: Order[];

  // support
  tickets: Ticket[];
  activeTicketId: string;
  handoffDone: boolean;

  // guest
  guestView: GuestView;
  guestStep: GuestStep;
  guestPhone: string;
  guestOtp: string[];
  guestPartySize: number;
  queuePos: number;
  queueState: QueueState;

  // settings (config)
  settingsTab: SettingsTab;
  location: LocationCfg;
  sections: SectionCfg[];
  settings: SettingsCfg;

  // live-backend integration (set by bootstrapHospitality once the adapter answers)
  backendLive: boolean;
  apiLocationId: string | null;
  // true once the dashboard SSE stream has delivered a real revenueToday — from then
  // on the sim must stop nudging it, or a real payment gets overwritten within seconds.
  revenueLive: boolean;

  // ── actions ──
  ease: () => void;
  pushEvent: (icon: string, tone: string, text: string) => void;
  /** Caches the current KPI counters + feed to localStorage — called after every
   *  SSE frame and every pushEvent, so a reload seeds from the last-known values
   *  instead of flashing back to zero/empty. */
  persistDash: () => void;

  // floor
  toggleEditLayout: () => void;
  resetLayout: () => void;
  moveTable: (id: string, x: number, y: number) => void;
  /** Set absolute positions for many tables at once (whole-section drag). */
  setTablePositions: (positions: Record<string, { x: number; y: number }>) => void;
  /** Replace the waitlist with the real line fetched from SK Queue (queue_live). */
  setWaitlist: (list: WaitlistEntry[]) => void;
  /** Reflow all tables into a clean non-overlapping grid for the given canvas width. */
  autoArrange: (canvasW: number) => void;
  /** Re-pack section blocks so their zones never overlap, PRESERVING each section's
   *  current internal tile arrangement. Called after a drag so growing one section
   *  pushes the others out of the way instead of overlapping them. */
  reflowSections: (canvasW: number) => void;
  /** AABB collision resolution for section zones — called after a whole-section
   *  drag. Keeps `anchorSection` (the one the operator just dropped) fixed and
   *  pushes any zone it now overlaps out of the way by the overlap + a fixed
   *  gap; re-checks all pairs until stable so a push that creates a new
   *  collision elsewhere gets resolved too. Deterministic for a given layout. */
  resolveSectionOverlaps: (canvasW: number, anchorSection: string) => void;
  persistPositions: () => void;
  selectTable: (id: string) => void;
  clearSelect: () => void;
  setFloorFilter: (name: string) => void;
  startCombine: (id: string) => void;
  seatWalkIn: (tableId: string, wid: string | null, guest?: { name: string; partySize: number }) => void;
  quickSeat: (wid: string) => void;
  markSettled: (id: string) => void;
  markCleaned: (id: string) => void;
  seatReserved: (id: string) => void;

  // reservations
  setReservations: (list: Reservation[]) => void;
  /** Reconcile floor-table status against the real reservation list from skcal:
   *  a free table with a confirmed reservation allocated to it shows 'reserved'
   *  (blocked from walk-in seating); a 'reserved' table whose reservation is gone
   *  (cancelled) flips back to 'free'. Never overrides 'seated'/'dirty' — physical
   *  state always wins over a booking record. */
  applyReservationHolds: (list: Reservation[]) => void;
  replaceReservationId: (tempId: string, realId: string) => void;
  confirmDraft: () => void;
  declineDraft: () => void;
  setResForm: (k: keyof ResForm, v: string | number) => void;
  /** Set the guest (name/phone/contactId) WITHOUT clearing the chosen time slot. */
  setResGuest: (v: { name: string; phone: string; contactId?: string }) => void;
  pickSlot: (time: string, status: string) => void;
  confirmBooking: () => void;
  cancelReservation: (id: string) => void;
  confirmReservation: (id: string) => void;

  // orders
  setActiveOrder: (id: string) => void;
  /** Find this table's open order (via its orderId), or stub a new local one
   *  — returns the order id either way. The stub has no real billing
   *  document yet; the first item added creates one and replaceOrderId
   *  adopts the real id. */
  findOrCreateOrderForTable: (tableId: string, tableLabel: string, guests?: number) => string;
  /** Swap a local stub id for the real billing document id once the first
   *  item creates it — mirrors replaceReservationId. */
  replaceOrderId: (tempId: string, realId: string) => void;
  /** Record the REAL totals SK Billing returned for this order — the single
   *  source of truth the Bill card and the open-orders list both read from. */
  setOrderTotals: (orderId: string, totals: { subtotal: number; taxTotal: number; grandTotal: number }) => void;
  /** Replace an order's lines + totals with what billing actually has, after
   *  ANY mutation (create/add/remove/qty-change) — every one of those calls
   *  supersedes to a new document, so the local lines must be resynced from
   *  the response every time or they drift from the real (unmerged) billing
   *  lines, which is what caused the duplicate-line bug after a reload. */
  applyBillingDoc: (orderId: string, doc: { status?: string; subtotal?: string; tax_total?: string; grand_total?: string; lines?: { id?: string; description: string; quantity?: string; unit_price?: string }[] }) => void;
  /** Seed the orders array from real billing documents on bootstrap — the
   *  rehydration counterpart to hydrateConfig, so tables with an open order
   *  show their real lines/total again after a reload instead of "No open
   *  orders yet." Replaces whatever's there (only ever called once on load,
   *  before any local order exists). */
  hydrateOrders: (orders: Order[]) => void;
  setMenuQuery: (v: string) => void;
  setMenuCat: (c: string) => void;
  addItem: (name: string, price: number) => void;
  changeQty: (lid: string, delta: number) => void;
  removeLine: (lid: string) => void;
  acceptUpsell: () => void;
  declineUpsell: () => void;
  sendBill: () => void;

  // support
  setTickets: (list: Ticket[]) => void;
  upsertTicketDetail: (detail: Ticket) => void;
  openTicket: (id: string) => void;
  acceptHandoff: () => void;

  // guest
  setGuestView: (v: GuestView) => void;
  guestScan: () => void;
  setGuestPhone: (v: string) => void;
  guestGetCode: () => void;
  setGuestOtp: (otp: string[]) => void;
  setGuestPartySize: (n: number) => void;
  guestReset: () => void;

  // settings
  setSettingsTab: (t: SettingsTab) => void;
  setLocation: (k: keyof LocationCfg, v: string) => void;
  setSectionName: (id: string, v: string) => void;
  setSectionServer: (id: string, v: string) => void;
  /** Assign the SK People employee (id + display name) chosen as this section's server. */
  setSectionServerRef: (id: string, serverRef: string, serverName: string) => void;
  /** Record the persisted adapter row id so later edits PATCH instead of re-creating. */
  setSectionApiId: (id: string, apiId: string) => void;
  addSection: () => void;
  removeSection: (id: string) => void;
  addTable: (sectionName: string) => void;
  removeTable: (id: string) => void;
  setTableField: (id: string, k: keyof FloorTableState, v: unknown) => void;
  /** Record the persisted adapter row id so later table edits PATCH instead of no-op. */
  setTableApiId: (id: string, apiId: string) => void;
  setCombinePolicy: (v: CombinePolicy) => void;
  toggleUpsellRule: (id: string, on: boolean) => void;
  setUpsellField: (id: string, k: string, v: string | number) => void;
  addUpsellRule: () => void;
  removeUpsellRule: (id: string) => void;
  toggleService: (k: keyof SettingsCfg['service'], on: boolean) => void;
  setTiming: (k: keyof SettingsCfg['timing'], v: string) => void;

  // live-backend integration
  markBackendLive: (locationId: string) => void;
  hydrateConfig: (payload: { location?: Partial<LocationCfg>; sections?: SectionCfg[]; tables?: FloorTableState[]; settings?: Partial<SettingsCfg> }) => void;
}

export const useHospitalityStore = create<HospState>((set, get) => ({
  // KPIs move only when the dashboard SSE stream sends real values (see
  // hospitalitySync.startCounters) — no fabricated seed, no simulated drift.
  // Initial value comes from the last-known cache (see loadDashCache above)
  // so a reload doesn't flash back to zero/empty while the stream reconnects.
  callsLive: savedDash.callsLive ?? 0,
  ordersToday: savedDash.ordersToday ?? 0,
  ordersOnWay: savedDash.ordersOnWay ?? 0,
  upsellPct: savedDash.upsellPct ?? 0,
  revenueToday: savedDash.revenueToday ?? 0,
  revenueLive: savedDash.revenueLive ?? false,
  tablesOccupied: savedDash.tablesOccupied ?? null,
  disp: {
    calls: savedDash.callsLive ?? 0, orders: savedDash.ordersToday ?? 0, onway: savedDash.ordersOnWay ?? 0,
    occ: savedDash.tablesOccupied ?? 0, upsell: savedDash.upsellPct ?? 0, revenue: savedDash.revenueToday ?? 0,
    beat: 0, bill: 0,
  },
  events: savedDash.events ?? [], // activity feed fills only from real operator actions (pushEvent)

  tables: [],
  selectedTableId: null,
  combineFrom: null,
  editLayout: false,
  draggingId: null,
  floorFilter: 'all',
  waitlist: [],

  reservations: [],
  draft: null,
  resForm: { size: 2, section: '', name: '', phone: '' },
  resSlot: null,

  activeOrderId: '',
  menuQuery: '',
  menuCat: 'All',
  orders: [],

  tickets: [],
  activeTicketId: '',
  handoffDone: false,

  guestView: 'join',
  guestStep: 'qr',
  guestPhone: '',
  guestOtp: ['', '', '', ''],
  guestPartySize: 2,
  queuePos: 0,
  queueState: 'waiting',

  settingsTab: 'venue',
  location: { org: '', name: '', timezone: '', open: '', close: '' },
  sections: [],
  settings: {
    combinePolicy: 'same-section',
    upsellRules: [],
    service: { autoWaitlistText: false, noShowHold: false, communalSeating: false, qrSelfJoin: false },
    // Sensible defaults so a fresh venue has meaningful turn times (small party 75m,
    // group 105m, +15m cleaning). Overwritten by the venue's saved turn_times on load.
    timing: { diningSmall: 75, diningGroup: 105, cleaningBuffer: 15, bookingGap: 0, reservedLeadMinutes: 30 },
    dashboardView: '',
  },

  backendLive: false,
  apiLocationId: null,

  // ── sim: 60ms eased tween toward the live KPI targets ──
  ease: () => {
    const s = get();
    const ao = s.orders.find((o) => o.id === s.activeOrderId) || s.orders[0];
    const beatTgt = ao && ao.upsell ? (ao.accepted ? ao.upsell.toPrice : ao.upsell.fromPrice) : 0;
    const billTgt = billSubOf(ao) * 1.05;
    const tgt: DispCounters = {
      calls: s.callsLive, orders: s.ordersToday, onway: s.ordersOnWay,
      occ: s.tablesOccupied ?? seatedCount(s.tables),
      upsell: s.upsellPct, revenue: s.revenueToday, beat: beatTgt, bill: billTgt,
    };
    const d = { ...s.disp };
    let changed = false;
    (Object.keys(tgt) as (keyof DispCounters)[]).forEach((k) => {
      const diff = tgt[k] - d[k];
      if (Math.abs(diff) > 0.5) { d[k] = d[k] + diff * 0.18; changed = true; }
      else if (d[k] !== tgt[k]) { d[k] = tgt[k]; changed = true; }
    });
    if (changed) set({ disp: d });
  },

  // No auto-dismiss — the feed card scrolls internally once it outgrows its box
  // (see Dashboard.tsx). Still capped well above any realistic shift's volume so
  // a very long session doesn't grow this array unbounded.
  pushEvent: (icon, tone, text) => {
    set((s) => ({ events: [{ icon, tone, text, t: fmtNow() }, ...s.events].slice(0, 200) }));
    get().persistDash();
  },

  persistDash: () => {
    const s = get();
    saveDashCache({
      callsLive: s.callsLive, ordersToday: s.ordersToday, ordersOnWay: s.ordersOnWay,
      upsellPct: s.upsellPct, revenueToday: s.revenueToday, revenueLive: s.revenueLive,
      tablesOccupied: s.tablesOccupied, events: s.events,
    });
  },

  // ── floor ──
  toggleEditLayout: () => {
    set((s) => ({ editLayout: !s.editLayout, selectedTableId: null, combineFrom: null }));
    toast(get().editLayout ? 'Drag tables to arrange — positions save to hosp_floor_table' : 'Layout locked', 'info');
  },
  resetLayout: () => {
    set((s) => ({ tables: s.tables.map((t) => ({ ...t, x: defaultPos[t.id]?.x ?? t.x, y: defaultPos[t.id]?.y ?? t.y })) }));
    try { localStorage.removeItem(FLOOR_POS_KEY); } catch { /* noop */ }
    toast('Floor layout reset to default', 'info');
  },
  moveTable: (id, x, y) => set((s) => ({ tables: s.tables.map((t) => (t.id === id ? { ...t, x, y } : t)), draggingId: id })),
  setTablePositions: (positions) => set((s) => ({
    tables: s.tables.map((t) => (positions[t.id] ? { ...t, ...positions[t.id] } : t)),
  })),
  setWaitlist: (list) => set({ waitlist: list }),
  autoArrange: (canvasW) => {
    set((s) => {
      const visible = s.tables.filter((t) => !t.mergedInto);
      if (!visible.length) return {};
      // Pack each SECTION as a padded "footprint" = its tiles (in a small grid)
      // PLUS the dashed zone's border padding and top label band. Shelf-pack the
      // footprints left-to-right, wrapping to a new shelf when the next won't fit.
      // Packing the *padded* footprint (not the raw tile block) is what guarantees
      // the zones themselves never overlap — the earlier bug placed tile blocks
      // that fit but whose zones/labels still collided. Snap to the 8px drag grid.
      const gapX = 18, gapY = 16;         // between tiles inside a section
      const ZP = 14, ZL = 26;             // zone padding (sides/bottom) + top label band
      const outerPad = 20, blockGap = 26; // canvas margin + gap between section footprints
      const SNAP = 8, MAX_COLS = 4;
      const snap = (v: number) => Math.round(v / SNAP) * SNAP;
      const cw = canvasW || 900;

      const bySection = new Map<string, typeof visible>();
      for (const t of visible) {
        if (!bySection.has(t.section)) bySection.set(t.section, []);
        bySection.get(t.section)!.push(t);
      }
      const ordered = [...new Set([...s.sections.map((x) => x.name), ...bySection.keys()])].filter((n) => bySection.has(n));

      // Build each section: tiles in a row-major grid, then wrap in the footprint.
      const blocks = ordered.map((name) => {
        const tiles = bySection.get(name)!.slice().sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
        const cols = Math.max(1, Math.min(MAX_COLS, tiles.length));
        const rows: typeof tiles[] = [];
        for (let i = 0; i < tiles.length; i += cols) rows.push(tiles.slice(i, i + cols));
        const local = new Map<string, { x: number; y: number }>();
        let yOff = 0, innerW = 0;
        for (const row of rows) {
          let xOff = 0;
          const rowH = Math.max(...row.map((t) => tileDims(t).h));
          for (const t of row) {
            local.set(t.id, { x: xOff, y: yOff });
            xOff += tileDims(t).w + gapX;
          }
          innerW = Math.max(innerW, xOff - gapX);
          yOff += rowH + gapY;
        }
        const innerH = yOff - gapY;
        // footprint: tiles + zone border on all sides + label band on top
        return { name, local, fw: innerW + 2 * ZP, fh: innerH + ZL + 2 * ZP };
      });

      // Shelf-pack footprints; tiles are placed inset by (ZP, ZL+ZP) so the zone
      // border + label always sit inside the reserved footprint → no collisions.
      const pos = new Map<string, { x: number; y: number }>();
      let curX = outerPad, curY = outerPad, shelfH = 0;
      for (const b of blocks) {
        if (curX > outerPad && curX + b.fw > cw - outerPad) { curX = outerPad; curY += shelfH + blockGap; shelfH = 0; }
        const ox = curX + ZP, oy = curY + ZL + ZP;
        for (const [id, l] of b.local) pos.set(id, { x: snap(ox + l.x), y: snap(oy + l.y) });
        curX += b.fw + blockGap;
        shelfH = Math.max(shelfH, b.fh);
      }
      return { tables: s.tables.map((t) => (pos.has(t.id) ? { ...t, ...pos.get(t.id)! } : t)) };
    });
    // Keep the tidied layout across reloads (no toast — draggingId is null).
    get().persistPositions();
  },
  reflowSections: (canvasW) => {
    set((s) => {
      const visible = s.tables.filter((t) => !t.mergedInto);
      if (!visible.length) return {};
      const ZP = 14, ZL = 26, outerPad = 20, blockGap = 26, SNAP = 8;
      const snap = (v: number) => Math.round(v / SNAP) * SNAP;
      const cw = canvasW || 900;

      const bySection = new Map<string, typeof visible>();
      for (const t of visible) {
        if (!bySection.has(t.section)) bySection.set(t.section, []);
        bySection.get(t.section)!.push(t);
      }
      // Order sections by their CURRENT position (top-edge band, then left edge) so
      // dragging a whole section to a new spot reorders it in the pack — a section
      // dropped top-right lands top-right, etc. Banding tolerates small y jitter.
      const band = (y: number) => Math.round(y / 60);
      const secMin = (n: string) => {
        const ts = bySection.get(n)!;
        return { y: Math.min(...ts.map((t) => t.y)), x: Math.min(...ts.map((t) => t.x)) };
      };
      const ordered = [...new Set([...s.sections.map((x) => x.name), ...bySection.keys()])]
        .filter((n) => bySection.has(n))
        .sort((a, b) => {
          const ma = secMin(a), mb = secMin(b);
          return band(ma.y) - band(mb.y) || ma.x - mb.x;
        });

      // Each section's footprint = its CURRENT tile bounding box (so the layout the
      // operator dragged is preserved) + zone padding + label band. Record each
      // tile's offset within that box so we can re-place it after packing.
      const blocks = ordered.map((name) => {
        const tiles = bySection.get(name)!;
        const minX = Math.min(...tiles.map((t) => t.x));
        const minY = Math.min(...tiles.map((t) => t.y));
        const maxX = Math.max(...tiles.map((t) => t.x + tileDims(t).w));
        const maxY = Math.max(...tiles.map((t) => t.y + tileDims(t).h));
        const offsets = new Map(tiles.map((t) => [t.id, { x: t.x - minX, y: t.y - minY }]));
        return { name, offsets, fw: (maxX - minX) + 2 * ZP, fh: (maxY - minY) + ZL + 2 * ZP };
      });

      // Shelf-pack the footprints; sections that grow push the rest along/down.
      const pos = new Map<string, { x: number; y: number }>();
      let curX = outerPad, curY = outerPad, shelfH = 0;
      for (const b of blocks) {
        if (curX > outerPad && curX + b.fw > cw - outerPad) { curX = outerPad; curY += shelfH + blockGap; shelfH = 0; }
        const ox = curX + ZP, oy = curY + ZL + ZP;
        for (const [id, o] of b.offsets) pos.set(id, { x: snap(ox + o.x), y: snap(oy + o.y) });
        curX += b.fw + blockGap;
        shelfH = Math.max(shelfH, b.fh);
      }
      return { tables: s.tables.map((t) => (pos.has(t.id) ? { ...t, ...pos.get(t.id)! } : t)) };
    });
    get().persistPositions();
  },
  resolveSectionOverlaps: (canvasW, anchorSection) => {
    set((s) => {
      const visible = s.tables.filter((t) => !t.mergedInto);
      if (!visible.length) return {};
      const bySection = new Map<string, typeof visible>();
      for (const t of visible) {
        if (!bySection.has(t.section)) bySection.set(t.section, []);
        bySection.get(t.section)!.push(t);
      }
      const names = [...new Set([...s.sections.map((x) => x.name), ...bySection.keys()])].filter((n) => bySection.has(n));
      if (names.length < 2) return {};

      // Each section's ORIGINAL bbox (from current table positions) plus an
      // accumulated push (dx, dy). The anchor never accumulates a push — every
      // resolution moves the section that ISN'T the one the operator just
      // dropped, so the drag they just did is preserved exactly.
      const baseBox = new Map(names.map((n) => [n, sectionBBox(bySection.get(n)!)!]));
      const shift = new Map(names.map((n) => [n, { dx: 0, dy: 0 }]));
      const boxOf = (n: string) => {
        const b = baseBox.get(n)!, sh = shift.get(n)!;
        return { x: b.x + sh.dx, y: b.y + sh.dy, w: b.w, h: b.h };
      };

      // A horizontal push only helps if the mover actually fits there without
      // running off the canvas (fixed width — it doesn't grow sideways). A
      // vertical push is always eventually feasible: pushing down never runs
      // out of room (Floor.tsx grows the canvas to fit the lowest tile), so
      // if there's no room to push up we push down instead. Returns null only
      // for the horizontal axis when neither direction fits.
      const tryPush = (mv: { x: number; y: number; w: number; h: number }, fx: typeof mv, axis: 'x' | 'y', amount: number) => {
        if (axis === 'x') {
          const dir = mv.x + mv.w / 2 >= fx.x + fx.w / 2 ? 1 : -1;
          const nx = mv.x + dir * amount;
          if (nx < 0 || nx + mv.w > canvasW) return null;
          return { dx: dir * amount, dy: 0 };
        }
        let dir = mv.y + mv.h / 2 >= fx.y + fx.h / 2 ? 1 : -1;
        if (dir < 0 && mv.y - amount < 0) dir = 1; // no room above — push down instead
        return { dx: 0, dy: dir * amount };
      };

      // Iterate pairwise collision checks to a fixed point: resolving one pair
      // can push a section into a THIRD section's space, so re-scan until a
      // full pass finds nothing left to fix (or we hit a sane iteration cap).
      const MAX_PASSES = 40;
      for (let pass = 0; pass < MAX_PASSES; pass++) {
        let moved = false;
        for (let i = 0; i < names.length; i++) {
          for (let j = i + 1; j < names.length; j++) {
            const nameA = names[i], nameB = names[j];
            const a = boxOf(nameA), b = boxOf(nameB);
            const overlapX = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
            const overlapY = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
            if (overlapX <= 0 || overlapY <= 0) continue; // AABB: no intersection

            // Never move the anchor — move the other one. If neither is the
            // anchor, always move `nameB` (later in the fixed `names` order)
            // so the same layout always resolves the same way regardless of
            // which section the operator actually dragged.
            const moverName = nameB === anchorSection ? nameA : nameB;
            const staticName = moverName === nameA ? nameB : nameA;
            const mv = boxOf(moverName), fx = boxOf(staticName);

            // Prefer whichever axis needs the smaller nudge to clear, but
            // fall back to the other axis if that push has no room (e.g. the
            // canvas is too narrow to shove the section sideways) — the
            // vertical fallback always succeeds, so this always resolves.
            const preferX = overlapX < overlapY;
            const push =
              (preferX ? tryPush(mv, fx, 'x', overlapX + SECTION_GAP) : null) ??
              tryPush(mv, fx, 'y', overlapY + SECTION_GAP) ??
              tryPush(mv, fx, 'x', overlapX + SECTION_GAP);
            if (!push) continue; // unreachable (y-axis always returns a push), kept for type-safety

            const sh = shift.get(moverName)!;
            shift.set(moverName, { dx: sh.dx + push.dx, dy: sh.dy + push.dy });
            moved = true;
          }
        }
        if (!moved) break;
      }

      const nextTables = s.tables.map((t) => {
        if (t.mergedInto) return t;
        const sh = shift.get(t.section);
        if (!sh || (sh.dx === 0 && sh.dy === 0)) return t;
        return { ...t, x: Math.round(t.x + sh.dx), y: Math.round(t.y + sh.dy) };
      });
      return { tables: nextTables };
    });
    get().persistPositions();
  },
  persistPositions: () => {
    const s = get();
    try {
      const m: Record<string, { x: number; y: number }> = {};
      s.tables.forEach((t) => { m[t.id] = { x: t.x, y: t.y }; });
      localStorage.setItem(FLOOR_POS_KEY, JSON.stringify(m));
    } catch { /* noop */ }
    const t = s.tables.find((x) => x.id === s.draggingId);
    if (t) toast(`${t.label} moved · pos_x ${t.x}, pos_y ${t.y} saved`, 'success');
    set({ draggingId: null });
  },
  selectTable: (id) => {
    const s = get();
    if (s.editLayout) return;
    if (s.combineFrom && s.combineFrom !== id) {
      const a = s.tables.find((t) => t.id === s.combineFrom);
      const b = s.tables.find((t) => t.id === id);
      if (a && b && a.section === b.section) {
        toast(`Combined ${a.label} + ${b.label} → ${a.seats + b.seats} seats`, 'success');
        get().pushEvent('combine', 'info', `Tables combined · ${a.label} + ${b.label} · ${a.seats + b.seats} seats`);
        set((st) => ({
          tables: st.tables.map((t) =>
            t.id === a.id ? { ...t, combinedWith: b.label, seats: a.seats + b.seats }
              : t.id === b.id ? { ...t, mergedInto: a.label, status: 'seated', party: '(combined)' } : t),
          combineFrom: null, selectedTableId: a.id,
        }));
        return;
      }
      toast('Tables must be in the same section to combine', 'warn');
      set({ combineFrom: null });
      return;
    }
    set((st) => ({ selectedTableId: st.selectedTableId === id ? null : id }));
  },
  clearSelect: () => set({ selectedTableId: null, combineFrom: null }),
  setFloorFilter: (name) => set({ floorFilter: name }),
  startCombine: (id) => { set({ combineFrom: id }); toast('Pick an adjacent table to combine with', 'info'); },

  seatWalkIn: (tableId, wid, guest) => {
    set((s) => {
      const w = wid ? s.waitlist.find((x) => x.id === wid) : null;
      const party = w ? `${w.name} · ${w.size}` : guest ? `${guest.name} · ${guest.partySize}` : 'Walk-in · 2';
      const lbl = s.tables.find((t) => t.id === tableId)?.label ?? tableId;
      toast(`Seated ${party} at ${lbl}`, 'success');
      get().pushEvent('users', 'success', `Walk-in seated · ${party} · ${lbl}`);
      return {
        // If this entry was already tracked on a different table (e.g. the
        // backend still shows it as CALLED so it reappeared in the
        // waitlist), clear that old table instead of leaving a duplicate
        // "Seated" tile behind — the same real entry can only be at one
        // table at a time.
        tables: s.tables.map((t) => {
          if (t.id === tableId) return { ...t, status: 'seated', party, since: fmtNow(), entryId: wid };
          if (wid && t.entryId === wid) return { ...t, status: 'free', party: null, since: null, entryId: null };
          return t;
        }),
        waitlist: w ? s.waitlist.filter((x) => x.id !== wid) : s.waitlist,
        selectedTableId: tableId,
      };
    });
  },

  quickSeat: (wid) => {
    set((s) => {
      const w = s.waitlist.find((x) => x.id === wid);
      if (!w) return {} as Partial<HospState>;
      // This exact entry may already be sitting at a table — e.g. it was
      // seated via "Seat walk-in" but the backend still reports it as
      // CALLED (resource reserved, not yet committed), so the next poll put
      // it right back in the waitlist. quickSeat must NOT pick a second,
      // different table for the same real entry — reuse the one it's
      // already on instead of guessing a fresh "free" one.
      const already = s.tables.find((t) => t.entryId === wid);
      const cand = already
        ?? s.tables.filter((t) => t.status === 'free' && t.seats >= w.size).sort((a, b) => a.seats - b.seats)[0]
        ?? s.tables.filter((t) => t.status === 'free').sort((a, b) => b.seats - a.seats)[0];
      if (!cand) { toast('No free table fits — combine two tables', 'warn'); return {} as Partial<HospState>; }
      toast(`Seated ${w.name} · ${w.size} at ${cand.label}`, 'success');
      get().pushEvent('users', 'success', `Queue allocated · ${w.name} · ${w.size} → ${cand.label}`);
      return {
        tables: s.tables.map((t) => (t.id === cand.id ? { ...t, status: 'seated', party: `${w.name} · ${w.size}`, since: fmtNow(), entryId: wid } : t)),
        waitlist: s.waitlist.filter((x) => x.id !== wid),
        selectedTableId: cand.id,
      };
    });
  },

  // "Mark bill settled" clears the table locally once the REAL settle+release
  // calls succeed (see Floor.tsx's handleMarkSettled — it calls this after,
  // not before, those backend calls land). No fake timers, no popping the
  // waitlist ourselves: SK Queue re-allocates the next waiting party on its
  // own the moment /entries/:id/done lands, and the next 5s poll picks that
  // up. Simulating it here would just fight the real data.
  markSettled: (id) => {
    const t = get().tables.find((x) => x.id === id);
    if (!t) return;
    set((s) => ({ tables: s.tables.map((x) => (x.id === id ? { ...x, status: 'free', party: null, since: null, entryId: null, orderId: null } : x)) }));
    toast(`${t.label} bill settled — freeing table`, 'success');
    get().pushEvent('check', 'success', `Bill settled · ${t.label} · resource freed`);
  },

  markCleaned: (id) => {
    const t = get().tables.find((x) => x.id === id);
    set((s) => ({ tables: s.tables.map((x) => (x.id === id ? { ...x, status: 'free' } : x)) }));
    if (t) toast(`${t.label} cleaned — now free`, 'success');
  },
  seatReserved: (id) => {
    const t = get().tables.find((x) => x.id === id);
    set((s) => ({ tables: s.tables.map((x) => (x.id === id ? { ...x, status: 'seated', party: x.party ? x.party.split(' · ')[0] : 'Guest', since: fmtNow() } : x)) }));
    if (t) { toast(`Reservation seated at ${t.label}`, 'success'); get().pushEvent('users', 'success', `Reservation seated · ${t.label}`); }
  },

  // ── reservations ──
  // Replace the list with the backend's (skcal is the source of truth). Called on
  // bootstrap so the "Tonight" panel survives a refresh.
  setReservations: (list) => set({ reservations: list }),
  applyReservationHolds: (list) => set((s) => {
    // Only block a table once its reservation is actually imminent — a booking
    // for later tonight shouldn't sit on the floor plan hours in advance. Once
    // held, it stays 'reserved' past start time too (late arrivals), until the
    // guest is seated or the reservation is cancelled. Lead time is a venue
    // setting (Settings → Timing & turns), not a fixed constant.
    const RESERVED_LEAD_MS = Math.max(0, s.settings.timing.reservedLeadMinutes) * 60 * 1000;
    const now = Date.now();
    // A combined join reports its table as 'MH2+MH3' — split so each member table
    // in the join is held, not just a literal (never-matching) compound label.
    const held = new Map<string, Reservation>();
    for (const r of list) {
      if (r.status !== 'confirmed' || !r.table) continue;
      if (new Date(r.time).getTime() - now > RESERVED_LEAD_MS) continue; // too far out — stays Free
      for (const label of r.table.split('+')) held.set(label, r);
    }
    return {
      tables: s.tables.map((t) => {
        const r = held.get(t.label);
        if (r && t.status === 'free') {
          return { ...t, status: 'reserved', party: `${r.name} · ${r.size}` };
        }
        if (!r && t.status === 'reserved') {
          return { ...t, status: 'free', party: null };
        }
        return t;
      }),
    };
  }),
  // Swap an optimistic temp id for the real Calendar event id once the POST resolves,
  // so a later confirm/cancel targets the persisted booking.
  replaceReservationId: (tempId, realId) =>
    set((s) => ({ reservations: s.reservations.map((r) => (r.id === tempId ? { ...r, id: realId } : r)) })),
  confirmDraft: () => {
    const d = get().draft;
    if (!d) return;
    set((s) => ({
      reservations: [{ id: 'R' + Date.now(), name: d.name, size: d.size, time: d.time, section: d.section, status: 'confirmed', via: 'VOICE call' }, ...s.reservations],
      draft: null,
      tables: s.tables.map((t) => (t.id === 'G4' && t.status === 'free' ? { ...t, status: 'reserved', party: `${d.name.split(' ')[0]} · ${d.time.replace(/ ?[AP]M/, '')}` } : t)),
    }));
    toast(`Booked ${d.name} · ${d.size} · ${d.time}`, 'success');
    get().pushEvent('calendar', 'info', `Reservation confirmed · ${d.name} · ${d.time}`);
  },
  declineDraft: () => { set({ draft: null }); toast('Draft dismissed', 'info'); },

  setResForm: (k, v) => set((s) => ({ resForm: { ...s.resForm, [k]: v }, resSlot: null })),
  setResGuest: (v) => set((s) => ({ resForm: { ...s.resForm, name: v.name, phone: v.phone, contactId: v.contactId } })),
  pickSlot: (time, status) => {
    if (status === 'full') { toast('That slot collides — SK Calendar blocks overbooking', 'warn'); return; }
    set({ resSlot: time });
  },
  confirmBooking: () => {
    const { resForm: f, resSlot: slot } = get();
    if (!slot) { toast('Pick an available time first', 'warn'); return; }
    const name = f.name || 'Walk-in guest';
    // slot is an ISO datetime (from buildSlots) — show a readable time in the toast.
    const when = slot.includes('T') ? new Date(slot).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : slot;
    set((s) => ({
      reservations: [{ id: 'R' + Date.now(), name, size: f.size, time: slot, section: f.section, status: 'confirmed', via: 'Operator' }, ...s.reservations],
      resSlot: null,
      resForm: { ...s.resForm, name: '', phone: '', contactId: undefined },
    }));
    toast(`Booked ${name} · ${f.size} · ${when} · ${f.section}`, 'success');
    get().pushEvent('calendar', 'info', `Reservation confirmed · ${name} · ${when}`);
  },
  cancelReservation: (id) => {
    const r = get().reservations.find((x) => x.id === id);
    set((s) => ({ reservations: s.reservations.filter((x) => x.id !== id) }));
    if (r) { toast(`Cancelled ${r.name} · refund handled in SK Billing`, 'info'); get().pushEvent('x', 'neutral', `Reservation cancelled · ${r.name}`); }
  },
  confirmReservation: (id) => {
    set((s) => ({ reservations: s.reservations.map((x) => (x.id === id ? { ...x, status: 'confirmed' } : x)) }));
    const r = get().reservations.find((x) => x.id === id);
    if (r) toast(`Confirmed ${r.name}`, 'success');
  },

  // ── orders ──
  setActiveOrder: (id) => set({ activeOrderId: id }),
  findOrCreateOrderForTable: (tableId, tableLabel, guests) => {
    const existing = get().tables.find((t) => t.id === tableId)?.orderId;
    if (existing) { set({ activeOrderId: existing }); return existing; }
    const id = 'ord_' + Date.now();
    // Real assigned server for this table's section (SK People-backed —
    // see setSectionServerRef) — was hardcoded '' regardless of who's
    // actually running that section.
    const table = get().tables.find((t) => t.id === tableId);
    const server = get().sections.find((sec) => sec.name === table?.section)?.server ?? '';
    set((s) => ({
      orders: [
        { id, table: tableLabel, guests: guests ?? 2, server, status: 'open', accepted: false, declined: false, upsell: null, lines: [] },
        ...s.orders,
      ],
      activeOrderId: id,
      tables: s.tables.map((t) => (t.id === tableId ? { ...t, orderId: id } : t)),
    }));
    return id;
  },
  replaceOrderId: (tempId, realId) =>
    set((s) => ({
      orders: s.orders.map((o) => (o.id === tempId ? { ...o, id: realId } : o)),
      activeOrderId: s.activeOrderId === tempId ? realId : s.activeOrderId,
      tables: s.tables.map((t) => (t.orderId === tempId ? { ...t, orderId: realId } : t)),
    })),
  setOrderTotals: (orderId, totals) =>
    set((s) => ({
      orders: s.orders.map((o) => (o.id === orderId ? { ...o, subtotal: totals.subtotal, taxTotal: totals.taxTotal, grandTotal: totals.grandTotal } : o)),
    })),
  applyBillingDoc: (orderId, doc) =>
    set((s) => ({
      orders: s.orders.map((o) =>
        o.id === orderId
          ? {
              ...o,
              status: orderStatusFromDoc(doc.status),
              lines: (doc.lines ?? []).map((l, i) => ({
                id: l.id ?? `l-${orderId}-${i}`,
                name: l.description,
                qty: Math.round(Number(l.quantity ?? 1)),
                price: Number(l.unit_price ?? 0),
              })),
              subtotal: Number(doc.subtotal ?? 0),
              taxTotal: Number(doc.tax_total ?? 0),
              grandTotal: Number(doc.grand_total ?? 0),
            }
          : o,
      ),
    })),
  hydrateOrders: (orders) => set({ orders }),
  setMenuQuery: (v) => set({ menuQuery: v }),
  setMenuCat: (c) => set({ menuCat: c }),
  addItem: (name, price) => {
    const s = get();
    const o = s.orders.find((x) => x.id === s.activeOrderId);
    if (!o) return;
    set((st) => ({
      orders: st.orders.map((ord) => {
        if (ord.id !== st.activeOrderId) return ord;
        const ex = ord.lines.find((l) => l.name === name && !l.premium);
        const lines = ex
          ? ord.lines.map((l) => (l === ex ? { ...l, qty: l.qty + 1 } : l))
          : [...ord.lines, { id: 'l' + Date.now(), name, qty: 1, price }];
        return { ...ord, lines };
      }),
    }));
    toast(`Added ${name} to ${o.table}`, 'success');
  },
  changeQty: (lid, delta) => set((s) => ({
    orders: s.orders.map((o) => (o.id === s.activeOrderId ? { ...o, lines: o.lines.map((l) => (l.id === lid ? { ...l, qty: Math.max(1, l.qty + delta) } : l)) } : o)),
  })),
  removeLine: (lid) => set((s) => ({
    orders: s.orders.map((o) => (o.id === s.activeOrderId ? { ...o, lines: o.lines.filter((l) => l.id !== lid) } : o)),
  })),
  acceptUpsell: () => {
    const s = get();
    const o = s.orders.find((x) => x.id === s.activeOrderId);
    if (!o || !o.upsell) return;
    const up = o.upsell;
    set((st) => ({
      orders: st.orders.map((ord) => (ord.id === o.id
        ? { ...ord, accepted: true, declined: false, lines: ord.lines.map((l) => (l.base ? { ...l, name: up.toItem, price: up.toPrice, premium: true } : l)) }
        : ord)),
    }));
    toast('Upsell applied · bill updated in SK Billing', 'success');
    get().pushEvent('star', 'warn', `Upsell accepted · ${up.fromItem} → ${up.toItem} · +₹${up.toPrice - up.fromPrice}`);
  },
  declineUpsell: () => {
    set((s) => ({ orders: s.orders.map((o) => (o.id === s.activeOrderId ? { ...o, declined: true } : o)) }));
    toast('Upsell declined', 'info');
  },
  sendBill: () => {
    const s = get();
    const o = s.orders.find((x) => x.id === s.activeOrderId);
    if (!o) return;
    set((st) => ({ orders: st.orders.map((ord) => (ord.id === o.id ? { ...ord, status: 'bill_sent' } : ord)) }));
    const total = billSubOf(o) * 1.05;
    toast(`Bill sent to ${o.table} · ₹${Math.round(total).toLocaleString('en-IN')} · GST-correct`, 'success');
    get().pushEvent('receipt', 'success', `Bill issued · ${o.table} · ₹${Math.round(total).toLocaleString('en-IN')}`);
  },

  // ── support ──
  // Replace the list from sk-tickets (list rows have no thread yet).
  setTickets: (list) => set((s) => ({ tickets: list, activeTicketId: s.activeTicketId || list[0]?.id || '' })),
  // Merge a fetched ticket's full detail (summary + thread) into the list row.
  upsertTicketDetail: (detail) => set((s) => ({
    tickets: s.tickets.some((t) => t.id === detail.id)
      ? s.tickets.map((t) => (t.id === detail.id ? { ...t, ...detail } : t))
      : [detail, ...s.tickets],
  })),
  openTicket: (id) => set({ activeTicketId: id }),
  acceptHandoff: () => {
    const id = get().activeTicketId;
    set((s) => ({
      handoffDone: true,
      tickets: s.tickets.map((t) => (t.id === id
        ? { ...t, status: 'resolved', by: 'Agent', timeline: [...t.timeline, { who: 'agent', text: 'Agent here — I have the full context and will take it from here.' }] }
        : t)),
    }));
    toast('Handoff accepted · context carried over', 'success');
    get().pushEvent('agent', 'info', 'Human handoff accepted · context carried over');
  },

  // ── guest ──
  setGuestView: (v) => set({ guestView: v }),
  guestScan: () => set({ guestStep: 'phone' }),
  setGuestPhone: (v) => set({ guestPhone: v }),
  // Advances to the OTP step. The actual OTP request/verify calls (requestGuestOtp/
  // verifyGuestOtp) live in Guest.tsx; this just drives the local step transition.
  guestGetCode: () => {
    set({ guestStep: 'otp' });
    toast('OTP sent via SMS', 'info');
  },
  setGuestOtp: (otp) => set({ guestOtp: otp }),
  setGuestPartySize: (n) => set({ guestPartySize: Math.max(1, n) }),
  guestReset: () => set({ guestStep: 'qr', guestOtp: ['', '', '', ''], guestPartySize: 2, queuePos: 0, queueState: 'waiting' }),

  // ── settings ──
  setSettingsTab: (t) => set({ settingsTab: t }),
  setLocation: (k, v) => set((s) => ({ location: { ...s.location, [k]: v } })),
  setSectionName: (id, v) => set((s) => {
    const old = s.sections.find((x) => x.id === id)?.name;
    return {
      sections: s.sections.map((x) => (x.id === id ? { ...x, name: v } : x)),
      tables: s.tables.map((t) => (t.section === old ? { ...t, section: v } : t)),
    };
  }),
  setSectionServer: (id, v) => { set((s) => ({ sections: s.sections.map((x) => (x.id === id ? { ...x, server: v } : x)) })); toast('Server reassigned · SK People ref updated', 'success'); },
  setSectionServerRef: (id, serverRef, serverName) => set((s) => ({ sections: s.sections.map((x) => (x.id === id ? { ...x, serverRef, server: serverName } : x)) })),
  setSectionApiId: (id, apiId) => set((s) => ({ sections: s.sections.map((x) => (x.id === id ? { ...x, apiId } : x)) })),
  setTableApiId: (id, apiId) => set((s) => ({ tables: s.tables.map((x) => (x.id === id ? { ...x, apiId } : x)) })),
  addSection: () => {
    set((s) => {
      const n = s.sections.length + 1;
      // No fake server/serverRef seed — server is assigned from real SK People
      // staff via the picker; serverRef stays empty until chosen (adapter accepts none).
      return { sections: [...s.sections, { id: 'sec_' + Date.now(), name: 'New section ' + n, prefix: 'S' + n, server: '', serverRef: '' }] };
    });
    toast('Section added — name it and add tables', 'success');
  },
  removeSection: (id) => {
    const sec = get().sections.find((x) => x.id === id);
    if (!sec) return;
    const has = get().tables.filter((t) => t.section === sec.name).length;
    if (has) { toast(`Move or remove ${sec.name}'s ${has} table(s) first`, 'warn'); return; }
    set((s) => ({ sections: s.sections.filter((x) => x.id !== id) }));
    toast(`${sec.name} deactivated`, 'info');
  },
  addTable: (sectionName) => {
    set((s) => {
      const sec = s.sections.find((x) => x.name === sectionName);
      const pre = sec?.prefix || (sectionName[0] || 'T').toUpperCase();
      const nums = s.tables.filter((t) => t.label.startsWith(pre)).map((t) => parseInt(t.label.slice(pre.length), 10) || 0);
      const label = pre + ((nums.length ? Math.max(...nums) : 0) + 1);
      const sibs = s.tables.filter((t) => t.section === sectionName);
      const baseX = sibs.length ? Math.min(...sibs.map((t) => t.x)) : 24;
      const maxY = sibs.length ? Math.max(...sibs.map((t) => t.y + t.h)) : 40;
      const nt: FloorTableState = {
        id: 'tbl_' + Date.now(), label, section: sectionName, server: sec?.server ?? '', seats: 4, shape: 'square',
        x: baseX, y: Math.min(330, maxY + 16), w: 74, h: 74, status: 'free', party: null, combinable: true,
      };
      return { tables: [...s.tables, nt] };
    });
    toast(`Table added to ${sectionName}`, 'success');
  },
  removeTable: (id) => {
    const t = get().tables.find((x) => x.id === id);
    if (!t) return;
    if (t.status === 'seated') { toast(`${t.label} is occupied — settle it first`, 'warn'); return; }
    set((s) => ({ tables: s.tables.filter((x) => x.id !== id) }));
    toast(`${t.label} removed from floor`, 'info');
  },
  setTableField: (id, k, v) => {
    set((s) => ({ tables: s.tables.map((t) => (t.id === id ? { ...t, [k]: v } : t)) }));
    const t = get().tables.find((x) => x.id === id);
    if (t) toast(`${t.label} · ${String(k)} updated — floor plan synced`, 'success');
  },
  setCombinePolicy: (v) => { set((s) => ({ settings: { ...s.settings, combinePolicy: v } })); toast('Policy saved', 'success'); },
  toggleUpsellRule: (id, on) => {
    set((s) => ({ settings: { ...s.settings, upsellRules: s.settings.upsellRules.map((r) => (r.id === id ? { ...r, on } : r)) } }));
    toast(on ? 'Rule enabled · Catalog ref active' : 'Rule disabled', 'info');
  },
  setUpsellField: (id, k, v) => set((s) => ({ settings: { ...s.settings, upsellRules: s.settings.upsellRules.map((r) => (r.id === id ? { ...r, [k]: v } : r)) } })),
  addUpsellRule: () => {
    set((s) => {
      const rule = { id: 'CAT-RULE-NEW-' + Date.now().toString(36).toUpperCase().slice(-4), name: 'New upsell rule', on: false, fromItem: 'Chicken Dum Biryani', fromPrice: 200, toItem: 'Prawn Dum Biryani', toPrice: 250 };
      return { settings: { ...s.settings, upsellRules: [...s.settings.upsellRules, rule] } };
    });
    toast('Rule added — edit the trigger & suggestion', 'success');
  },
  removeUpsellRule: (id) => { set((s) => ({ settings: { ...s.settings, upsellRules: s.settings.upsellRules.filter((r) => r.id !== id) } })); toast('Rule removed', 'info'); },
  toggleService: (k, on) => { set((s) => ({ settings: { ...s.settings, service: { ...s.settings.service, [k]: on } } })); toast('Setting ' + (on ? 'on' : 'off'), 'info'); },
  setTiming: (k, v) => set((s) => ({ settings: { ...s.settings, timing: { ...s.settings.timing, [k]: Math.max(0, parseInt(v, 10) || 0) } } })),

  // ── live-backend integration ──
  markBackendLive: (locationId) => set({ backendLive: true, apiLocationId: locationId }),
  hydrateConfig: (payload) => set((s) => {
    let tables = s.tables;
    if (payload.tables) {
      // Record the backend positions as the "Reset layout" baseline, then apply
      // any saved local drag overrides for display.
      defaultPos = Object.fromEntries(payload.tables.map((t) => [t.id, { x: t.x, y: t.y }]));
      tables = payload.tables.map((t) => (savedPos[t.id] ? { ...t, x: savedPos[t.id].x, y: savedPos[t.id].y } : { ...t }));
    }
    return {
      location: payload.location ? { ...s.location, ...payload.location } : s.location,
      sections: payload.sections ?? s.sections,
      tables,
      settings: payload.settings ? { ...s.settings, ...payload.settings } : s.settings,
    };
  }),
}));
